import { useState, useEffect } from "react";
import { getStepDisplayName } from "@/lib/stepUtils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, ArrowRight, ArrowLeft, CalendarIcon, Check, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import type { ProductUnit, Run, Project } from "@/types/chemistry";

const STEP_LABELS = ["Details", "Steps", "Notes & Review"] as const;

function sortStepsByLabel(a: { label: string }, b: { label: string }) {
  const parse = (label: string) => {
    const match = label.replace(/^Step\s*/i, "").match(/^(\d+)(.*)$/);
    return match ? { num: parseInt(match[1], 10), suffix: match[2].toLowerCase() } : { num: 0, suffix: label.toLowerCase() };
  };
  const pa = parse(a.label);
  const pb = parse(b.label);
  if (pa.num !== pb.num) return pa.num - pb.num;
  return pa.suffix.localeCompare(pb.suffix);
}

interface RunSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: Run;
  project: Project;
  onSave: (updates: Partial<Run>) => void;
}

export function RunSettingsModal({ open, onOpenChange, run, project, onSave }: RunSettingsModalProps) {
  const [wizardStep, setWizardStep] = useState(0);
  const [title, setTitle] = useState(run.title);
  const [productAmount, setProductAmount] = useState(String(run.productAmount));
  const [productUnit, setProductUnit] = useState<ProductUnit>(run.productUnit);
  const [dateMode, setDateMode] = useState<"start" | "end">(run.deadline ? "end" : "start");
  const [selectedDate, setSelectedDate] = useState(run.deadline || run.startDate || "");
  const [notes, setNotes] = useState(run.notes || "");
  const [defaultStepYield] = useState(80);
  const [selectedStepIds, setSelectedStepIds] = useState<string[]>(run.selectedStepIds);
  const [stepOverrides, setStepOverrides] = useState<Record<string, { yield?: number; stepTimeDays?: number }>>(
    Object.fromEntries(
      Object.entries(run.stepOverrides || {}).map(([id, o]) => [id, {
        yield: o.yield,
        stepTimeDays: o.stepTimeDays,
      }])
    )
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [notIncludedExpanded, setNotIncludedExpanded] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setWizardStep(0);
      setTitle(run.title);
      setProductAmount(String(run.productAmount));
      setProductUnit(run.productUnit);
      setDateMode(run.deadline ? "end" : "start");
      setSelectedDate(run.deadline || run.startDate || "");
      setNotes(run.notes || "");
      setSelectedStepIds(run.selectedStepIds);
      setStepOverrides(
        Object.fromEntries(
          Object.entries(run.stepOverrides || {}).map(([id, o]) => [id, {
            yield: o.yield,
            stepTimeDays: o.stepTimeDays,
          }])
        )
      );
      setErrors({});
    }
  }, [open, run]);

  const validateStep0 = () => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = "Run title is required";
    const amount = parseFloat(productAmount);
    if (!productAmount || isNaN(amount) || amount <= 0) errs.productAmount = "Must be a positive number";
    if (!selectedDate) errs.date = "Please select a date";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (selectedStepIds.length === 0) errs.steps = "Select at least one step";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (wizardStep === 0 && !validateStep0()) return;
    if (wizardStep === 1 && !validateStep1()) return;
    setErrors({});
    setWizardStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setErrors({});
    setWizardStep((prev) => prev - 1);
  };

  const handleSave = () => {
    const mappedOverrides = Object.fromEntries(
      Object.entries(stepOverrides).map(([id, o]) => [id, {
        yield: o.yield,
        stepTimeDays: o.stepTimeDays,
      }])
    );

    onSave({
      title: title.trim(),
      productAmount: parseFloat(productAmount),
      productUnit,
      deadline: dateMode === "end" ? selectedDate : undefined,
      startDate: dateMode === "start" ? selectedDate : undefined,
      notes,
      assumedYield: computedOverallYield,
      selectedStepIds,
      stepOverrides: Object.keys(mappedOverrides).length > 0 ? mappedOverrides : undefined,
    });
    onOpenChange(false);
  };

  const updateStepOverride = (stepId: string, field: "yield" | "stepTimeDays", value: string) => {
    setStepOverrides((prev) => {
      const current = prev[stepId] || {};
      const numVal = value === "" ? undefined : parseFloat(value);
      const updated = { ...current, [field]: isNaN(numVal as number) ? undefined : numVal };
      if (updated.yield === undefined && updated.stepTimeDays === undefined) {
        const { [stepId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [stepId]: updated };
    });
  };

  const computedOverallYield = selectedStepIds.reduce((acc, sid) => {
    const stepYield = stepOverrides[sid]?.yield ?? defaultStepYield;
    return acc * (stepYield / 100);
  }, 1) * 100;
  const displayOverallYield = selectedStepIds.length > 0 ? Math.round(computedOverallYield * 10) / 10 : defaultStepYield;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] flex flex-col p-0 gap-0">
        <div className="px-6 pt-6 pb-4 border-b shrink-0">
          <h2 className="text-xl font-bold tracking-tight">Run Settings</h2>
          <p className="text-sm text-muted-foreground mt-1">Edit the configuration for this run</p>

          {/* Wizard step indicator */}
          <div className="flex items-center gap-2 mt-4">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    i < wizardStep ? "bg-primary text-primary-foreground" :
                    i === wizardStep ? "bg-primary text-primary-foreground" :
                    "bg-muted text-muted-foreground"
                  }`}>
                    {i < wizardStep ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <span className={`text-sm ${i === wizardStep ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                    {label}
                  </span>
                </div>
                {i < STEP_LABELS.length - 1 && (
                  <div className={`w-8 h-px ${i < wizardStep ? "bg-primary" : "bg-border"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Step 1: Details */}
          {wizardStep === 0 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Run Title *</Label>
                <Input id="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} className={errors.title ? "border-destructive" : ""} />
                {errors.title && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.title}</p>}
              </div>

              <div className="space-y-2">
                <Label>Target Compound</Label>
                <div className="h-10 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-mono">
                  {project.targetCompound || "Not set"}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-amount">Product Amount *</Label>
                <div className="flex gap-2">
                  <Input id="edit-amount" type="number" min="0" step="any" value={productAmount} onChange={(e) => setProductAmount(e.target.value)} className={`flex-1 ${errors.productAmount ? "border-destructive" : ""}`} />
                  <Select value={productUnit} onValueChange={(v) => setProductUnit(v as ProductUnit)}>
                    <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["mg", "g", "kg", "mL", "L"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {errors.productAmount && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.productAmount}</p>}
              </div>

            <div className="space-y-2">
              <Label>Scheduling *</Label>
              <p className="text-xs text-muted-foreground">Set either a start date or a deadline — the other will be calculated from step workdays.</p>
              <div className="flex items-center gap-3">
                <div className="relative inline-flex items-center rounded-md border border-input p-0.5 h-8">
                  <div className="absolute top-0.5 bottom-0.5 rounded bg-primary shadow-sm transition-all duration-300 ease-in-out" style={{ left: dateMode === "start" ? "2px" : "50%", width: "calc(50% - 2px)" }} />
                  <button type="button" onClick={() => { setDateMode("start"); setSelectedDate(""); }} className={cn("relative z-10 px-2.5 py-1 text-xs font-medium rounded transition-colors duration-300", dateMode === "start" ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>Start Date</button>
                  <button type="button" onClick={() => { setDateMode("end"); setSelectedDate(""); }} className={cn("relative z-10 px-2.5 py-1 text-xs font-medium rounded transition-colors duration-300", dateMode === "end" ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>Deadline</button>
                </div>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[200px] h-8 justify-start text-left font-normal text-sm", !selectedDate && "text-muted-foreground", errors.date && "border-destructive")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(parseISO(selectedDate), "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={selectedDate ? parseISO(selectedDate) : undefined} onSelect={(d) => { setSelectedDate(d ? format(d, "yyyy-MM-dd") : ""); setDatePickerOpen(false); }} initialFocus className={cn("p-3 pointer-events-auto")} />
                  </PopoverContent>
                </Popover>
              </div>
              {errors.date && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.date}</p>}
            </div>
            </div>
          )}

          {/* Step 2: Steps */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Steps to Include *</Label>
                <p className="text-xs text-muted-foreground">Configure per-step yield and time estimates for included steps.</p>
              </div>

              <div className="rounded-lg border bg-card">
                <div className="flex items-center gap-3 px-3 py-2 bg-muted/30 border-b">
                  <span className="text-xs font-medium text-muted-foreground flex-1">Included ({selectedStepIds.length})</span>
                  <span className="text-xs font-medium text-muted-foreground w-20 text-center">Yield %</span>
                  <span className="text-xs font-medium text-muted-foreground w-20 text-center">Workdays</span>
                </div>
                {project.steps.filter((s) => selectedStepIds.includes(s.id)).length === 0 && (
                  <div className="px-3 py-4 text-xs text-muted-foreground/60 text-center italic">No steps selected</div>
                )}
                {[...project.steps].filter((s) => selectedStepIds.includes(s.id)).sort(sortStepsByLabel).map((step) => {
                  const override = stepOverrides[step.id];
                  return (
                    <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 bg-accent/30">
                      <Checkbox id={`edit-step-${step.id}`} checked={true} onCheckedChange={() => setSelectedStepIds((prev) => prev.filter((id) => id !== step.id))} />
                      <label htmlFor={`edit-step-${step.id}`} className="flex-1 cursor-pointer min-w-0">
                        <span className="text-sm font-medium">{getStepDisplayName(step)}</span>
                        {step.notes && <span className="text-xs text-muted-foreground ml-2 truncate">— {step.notes}</span>}
                      </label>
                      <Input type="number" min="1" max="100" placeholder={String(defaultStepYield)} value={override?.yield ?? ""} onChange={(e) => updateStepOverride(step.id, "yield", e.target.value)} className="w-20 h-7 text-xs text-center" />
                      <Input type="number" min="0" placeholder="—" value={override?.stepTimeDays ?? ""} onChange={(e) => updateStepOverride(step.id, "stepTimeDays", e.target.value)} className="w-20 h-7 text-xs text-center" />
                    </div>
                  );
                })}
              </div>

              {project.steps.filter((s) => !selectedStepIds.includes(s.id)).length > 0 && (
                <div className="rounded-lg border bg-card">
                  <button type="button" onClick={() => setNotIncludedExpanded((prev) => !prev)} className="flex items-center gap-2 px-3 py-2 w-full text-left bg-muted/30 hover:bg-muted/40 transition-colors">
                    {notIncludedExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    <span className="text-xs font-medium text-muted-foreground">Not included ({project.steps.filter((s) => !selectedStepIds.includes(s.id)).length})</span>
                  </button>
                  {notIncludedExpanded && [...project.steps].filter((s) => !selectedStepIds.includes(s.id)).sort(sortStepsByLabel).map((step) => (
                    <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 border-t bg-accent/30">
                      <Checkbox id={`edit-step-ex-${step.id}`} checked={false} onCheckedChange={() => setSelectedStepIds((prev) => [...prev, step.id])} />
                      <label htmlFor={`edit-step-ex-${step.id}`} className="flex-1 cursor-pointer min-w-0">
                        <span className="text-sm font-medium text-muted-foreground">{getStepDisplayName(step)}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {errors.steps && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.steps}</p>}

              <div className="space-y-1 pt-2">
                <Label>Overall Yield</Label>
                <div className="h-10 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-medium w-32">{displayOverallYield}%</div>
                <p className="text-xs text-muted-foreground">Calculated from per-step yields (default {defaultStepYield}%)</p>
              </div>
            </div>
          )}

          {/* Step 3: Notes & Review */}
          {wizardStep === 2 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label>Review</Label>
                <div className="rounded-lg border divide-y">
                  {[
                    ["Title", title],
                    ["Target Compound", project.targetCompound || "—"],
                    ["Product Amount", `${productAmount} ${productUnit}`],
                    [dateMode === "start" ? "Start Date" : "Deadline", selectedDate || "—"],
                    ["Steps", `${selectedStepIds.length} of ${project.steps.length}`],
                    ["Overall Yield", `${displayOverallYield}%`],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between px-4 py-2.5 bg-accent/30">
                      <span className="text-sm text-muted-foreground">{label}</span>
                      <span className="text-sm font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes (optional)</Label>
                <Textarea id="edit-notes" placeholder="Additional context for this run..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between px-6 py-4 border-t shrink-0">
          <div>
            {wizardStep === 0 ? (
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            ) : (
              <Button variant="outline" onClick={handleBack} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
          </div>
          <div>
            {wizardStep < 2 ? (
              <Button onClick={handleNext} className="gap-1.5">
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleSave} className="gap-1.5">
                <Check className="h-4 w-4" /> Save Changes
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
